// Fase 30 — Tombstones genéricos para tabelas sincronizadas pelo Firebase.
// Quando um item é excluído pelo admin, registramos um tombstone local
// que persiste e é consultado pelo `firebaseSync.attachTableListener`
// para impedir ressurreições após relogin/refresh.
//
// O sync usa a tabela como namespace: `deletedTombstones__{table}`.
//
// v7.46 — Migrado para suportar `stableId` (UUID gerado na criação do
// item, consistente entre devices) em vez de só `localId` (auto-increment
// do Dexie, pode colidir entre devices). Especificamente para `menuItems`,
// dois devices podem ter assigned o MESMO local id pra itens lógicos
// DIFERENTES (criados offline simultaneamente). Sem stableId, deletar
// id=5 em A apagaria um item completamente diferente em B.
//
// Formato do registro: { stableId?: string, id?: number, at: ISO, ...extra }
// Pelo menos um de stableId/id deve estar presente. Para `menuItems`
// sempre temos ambos (id local + stableId UUID). Para outras tabelas
// (commands, deliveryOrders, kdsQueue) o stableId fica opcional, mas o
// firebaseSync deveria começar a passá-lo quando disponível.

import db from '@/db';

const KEY_PREFIX = 'deletedTombstones__';
const MAX_PER_TABLE = 1000;

function keyFor(table) {
  return `${KEY_PREFIX}${table}`;
}

export async function getTombstones(table) {
  const cfg = await db.config.get(keyFor(table));
  return Array.isArray(cfg?.value) ? cfg.value : [];
}

/**
 * Set de localIds tombstoned (compat back). Continuar usado pelas
 * tabelas commands/deliveryOrders/kdsQueue.
 */
export async function getTombstonedIds(table) {
  const list = await getTombstones(table);
  return new Set(
    list
      .map((t) => (typeof t === 'object' ? t.id : t))
      .filter((id) => id != null)
      .map((id) => Number(id)),
  );
}

/**
 * v7.46 — Set de stableIds (strings UUID) tombstoned. Usado para `menuItems`,
 * onde o local id pode colidir entre devices. Vazio se nenhum tombstone
 * desta tabela tem stableId.
 */
export async function getTombstonedStableIds(table) {
  const list = await getTombstones(table);
  return new Set(
    list
      .map((t) => (typeof t === 'object' ? t.stableId : null))
      .filter((s) => typeof s === 'string' && s.length > 0),
  );
}

/**
 * Check by localId (back-compat).
 */
export async function isTombstoned(table, id) {
  const ids = await getTombstonedIds(table);
  return ids.has(Number(id));
}

/**
 * v7.71 — Map localId → timestamp(ms) da EXCLUSÃO real.
 * Prefere `deletedAt` (momento autoritativo da exclusão, preservado entre
 * devices) — `at` é re-carimbado na ingestão local de tombstones remotos e
 * NÃO representa quando o item foi excluído. Sem timestamp: Infinity (mata).
 */
export async function getTombstoneTimes(table) {
  const list = await getTombstones(table);
  const map = new Map();
  for (const t of list) {
    const id = typeof t === 'object' ? t.id : t;
    if (id == null) continue;
    const src = typeof t === 'object' ? (t.deletedAt || t.at) : null;
    const ms = src ? new Date(src).getTime() : Infinity;
    map.set(Number(id), Number.isFinite(ms) ? ms : Infinity);
  }
  return map;
}

/**
 * v7.71 — Um tombstone só MATA um registro se for mais novo que a criação
 * dele (com 5s de tolerância a skew de relógio entre devices). Registro
 * criado DEPOIS da exclusão do id antigo = id reutilizado → sobrevive.
 */
export function tombstoneKillsRecord(tombAtMs, recordCreatedAtMs) {
  if (tombAtMs == null || tombAtMs === undefined) return false;
  if (!recordCreatedAtMs) return true; // sem createdAt: comportamento antigo
  return tombAtMs >= recordCreatedAtMs - 5000;
}

/**
 * v7.71 — Remove um tombstone stale (por localId) da lista local.
 */
export async function removeTombstone(table, id) {
  const list = await getTombstones(table);
  const next = list.filter((t) => {
    const lid = typeof t === 'object' ? t.id : t;
    return Number(lid) !== Number(id);
  });
  if (next.length !== list.length) {
    await db.config.put({ key: keyFor(table), value: next });
  }
}

/**
 * v7.46 — Check by stableId.
 */
export async function isStableTombstoned(table, stableId) {
  if (!stableId) return false;
  const ids = await getTombstonedStableIds(table);
  return ids.has(String(stableId));
}

/**
 * v7.46 — API flexível: aceita (table, idOrObj, extra).
 *
 *   addTombstone('commands', 17)                  // legado: só localId
 *   addTombstone('menuItems', { stableId, id })   // novo: ambos
 *   addTombstone('menuItems', { stableId, id, name: 'Picanha' })
 *
 * Dedup por stableId (preferencialmente) ou por id. Se o item já existe
 * no array, mescla os campos novos (preserva metadados).
 */
export async function addTombstone(table, idOrObj, extra = {}) {
  let record;
  if (idOrObj && typeof idOrObj === 'object') {
    record = { ...idOrObj, ...extra };
  } else {
    record = { id: idOrObj, ...extra };
  }
  if (record.id != null) record.id = Number(record.id);
  if (!record.at) record.at = new Date().toISOString();

  const list = await getTombstones(table);

  // Dedup priority: stableId > localId. Se já existe, mescla.
  let idx = -1;
  if (record.stableId) {
    idx = list.findIndex((t) => typeof t === 'object' && t.stableId === record.stableId);
  }
  if (idx === -1 && record.id != null) {
    idx = list.findIndex((t) => {
      const lid = typeof t === 'object' ? t.id : t;
      return Number(lid) === Number(record.id);
    });
  }

  if (idx >= 0) {
    const cur = typeof list[idx] === 'object' ? list[idx] : { id: list[idx] };
    list[idx] = { ...cur, ...record };
  } else {
    list.push(record);
  }
  const trimmed = list.slice(-MAX_PER_TABLE);
  await db.config.put({ key: keyFor(table), value: trimmed });
}

export async function clearTombstones(table) {
  await db.config.delete(keyFor(table));
}

// Lista de tabelas que ganham proteção contra ressurreição.
// Mantemos sincronizada com SYNCED_TABLES do firebaseSync.
// v7.40 — `menuItems` adicionado: pratos deletados pelo admin não voltam
// mais em devices novos (tombstone sincronizado via Firebase).
export const PROTECTED_TABLES = ['commands', 'deliveryOrders', 'kdsQueue', 'menuItems'];

// v7.46 — Tabelas onde o stableId é AUTORITATIVO para tombstones (devices
// independentes podem ter local ids colidindo). Para essas tabelas, o
// firebaseSync usa o stableId como chave em `__tombstones__/{table}/{stableId}`.
// O default (commands/deliveryOrders/kdsQueue) continua usando localId.
// v7.85 (#207625) -- deliveryOrders/kdsQueue/commands migrados para chave
// estavel (stableId UUID), como menuItems. Tombstones passam a casar por
// stableId em vez do id local reutilizavel -> imune a ressurreicao por
// reuso de id / refresh de createdAt (bug do 'forced sync' que reinjeta
// pedidos apagados) e a colisao de id entre aparelhos.
export const STABLE_ID_TABLES = ['menuItems', 'deliveryOrders', 'kdsQueue', 'commands'];
